/**
 * SnowMachineControls Component
 *
 * Compact always-on strip (rate + wind + actions). Triangle reveals
 * advanced launcher / pattern / particle settings.
 */

import { useEffect, useRef, useState } from 'react';
import { getActiveTheme } from '../config/seasonalFeatures';
import { FIREWORK_EFFECTS, VOLLEY_PATTERNS, SHELL_CALIBERS } from '../snowmachine/fireworkEffects';
import './SnowMachine.css';

const FW_EFFECTS = Object.values(FIREWORK_EFFECTS);
const FW_PATTERNS = Object.values(VOLLEY_PATTERNS);
const FW_CALIBERS = Object.values(SHELL_CALIBERS);

export default function SnowMachineControls({ simulationRef, onControlsReady, theme: themeProp }) {
  const controlsRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const theme = themeProp || getActiveTheme();
  const isFireworks = theme.behavior === 'fireworks';
  const defaultSpawn = theme.defaultSpawnRate ?? 500;
  const shellEffects = isMobile
    ? FW_EFFECTS.filter((fx) => fx.id !== 'kamuro')
    : FW_EFFECTS;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const keyElements = ['clearBtn', 'rainBtn', 'flakesPerMinuteSlider'];
        const allExist = keyElements.every((id) => document.getElementById(id) !== null);
        if (allExist && onControlsReady) onControlsReady();
        else {
          setTimeout(() => {
            if (keyElements.every((id) => document.getElementById(id) !== null) && onControlsReady) {
              onControlsReady();
            }
          }, 200);
        }
      });
    });
  }, [onControlsReady]);

  const spawnSlider = (
    <div className="control-group">
      <label htmlFor="flakesPerMinuteSlider">
        {theme.spawnLabel}: <span id="flakesPerMinuteValue">{defaultSpawn}</span> (<span id="currentFlakesPerMinute">{defaultSpawn}</span>) <span id="variancePercentage"></span>
      </label>
      <input
        type="range"
        id="flakesPerMinuteSlider"
        min={theme.spawnRateMin}
        max={theme.spawnRateMax}
        defaultValue={defaultSpawn}
        step={theme.spawnRateStep}
      />
    </div>
  );

  const windSlider = (
    <div className="control-group">
      <label htmlFor="windSlider">
        Wind: <span id="windBaseValue">-1</span> (<span id="windValue">-1</span>) <span id="windVariancePercentage"></span>
      </label>
      <input type="range" id="windSlider" min="-25" max="25" defaultValue="-1" step="1" />
    </div>
  );

  const buttons = (
    <div className="button-row">
      <button id="clearBtn" type="button">Clear All</button>
      <button id="rainBtn" type="button">{theme.actionLabel}</button>
      {isFireworks && <button id="finaleBtn" type="button">Finale</button>}
      <button
        type="button"
        className={`controls-advanced-toggle${advancedOpen ? ' is-open' : ''}`}
        aria-expanded={advancedOpen}
        aria-controls="controlsAdvanced"
        onClick={() => setAdvancedOpen((v) => !v)}
        title={advancedOpen ? 'Hide advanced' : 'Show advanced'}
      >
        ▾
      </button>
    </div>
  );

  const advanced = (
    <div
      id="controlsAdvanced"
      className="controls-advanced"
      hidden={!advancedOpen}
    >
      {isFireworks ? (
        <div className="firework-mortar-panel" id="fireworkMortarPanel">
          <div className={`control-row${isMobile ? ' mobile-row' : ''}`}>
            <div className="control-group">
              <label>Launch battery</label>
              <div className="fw-toggle-row" id="fwBatteryRow">
                <button type="button" data-fw-battery="left">L</button>
                <button type="button" data-fw-battery="center" className="active">C</button>
                <button type="button" data-fw-battery="right">R</button>
              </div>
            </div>
            <div className="control-group">
              <label>Shell caliber</label>
              <div className="fw-toggle-row" id="fwCaliberRow">
                {FW_CALIBERS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-fw-caliber={c.id}
                    className={c.id === 'five' ? 'active' : undefined}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="control-group">
              <label>Shells per fire</label>
              <div className="fw-toggle-row" id="fwCountRow">
                {[1, 2, 3, 6, 9, 12, 18, 24, 36].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-fw-count={n}
                    className={n === 6 ? 'active' : undefined}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className={`control-row${isMobile ? ' mobile-row fw-mobile-split' : ''}`}>
            <div className={`control-group${isMobile ? ' fw-mobile-col' : ''}`}>
              <label>Shell type</label>
              <div className="fw-effect-grid" id="fwEffectGrid">
                {shellEffects.map((fx) => (
                  <button key={fx.id} type="button" data-fw-effect={fx.id}>
                    {fx.name}
                  </button>
                ))}
              </div>
            </div>
            <div className={`control-group${isMobile ? ' fw-mobile-col' : ''}`} id="fireworkPatternPanel">
              <label>Pattern demos</label>
              <div className="fw-effect-grid fw-pattern-grid" id="fwPatternGrid">
                {FW_PATTERNS.map((p) => (
                  <button key={`pat-${p.id}`} type="button" data-fw-pattern={p.id}>
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={`control-row${isMobile ? ' mobile-row' : ' three-column'}`}>
          <div className="control-column">
            <div className="control-group">
              <label htmlFor="minFallSpeedSlider">
                Min Fall Speed: <span id="minFallSpeedValue">0.5</span>
              </label>
              <input type="range" id="minFallSpeedSlider" min="0.1" max="8" defaultValue="0.5" step="0.1" />
            </div>
            <div className="control-group">
              <label htmlFor="maxFallSpeedSlider">
                Max Fall Speed: <span id="maxFallSpeedValue">3.0</span>
              </label>
              <input type="range" id="maxFallSpeedSlider" min="0.1" max="8" defaultValue="3.0" step="0.1" />
            </div>
          </div>
          <div className="control-column">
            <div className="control-group">
              <label htmlFor="smallestPiecesSlider">
                Smallest {theme.pieceLabel}: <span id="smallestPiecesValue">1 piece</span>
              </label>
              <input type="range" id="smallestPiecesSlider" min="1" max="20" defaultValue="1" step="1" />
            </div>
            <div className="control-group">
              <label htmlFor="biggestPiecesSlider">
                Biggest {theme.pieceLabel}: <span id="biggestPiecesValue">8 pieces</span>
              </label>
              <input type="range" id="biggestPiecesSlider" min="1" max="20" defaultValue="8" step="1" />
            </div>
          </div>
        </div>
      )}
      <div className={`attribution${isMobile ? ' mobile-top' : ''}`}>{theme.attribution}</div>
    </div>
  );

  return (
    <div className="controls" id="controls" ref={controlsRef}>
      <div className={`control-row${isMobile ? ' mobile-row' : ''}`}>
        {spawnSlider}
        {windSlider}
        {buttons}
      </div>
      {advanced}
    </div>
  );
}
